import {
  type QueryFunctionContext,
  type QueryObserverOptions,
  useQuery
} from 'react-query'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import { fetchQueryData } from 'hooks/use-gremlin-query'
import { type FailureFlagExperiment } from 'types/api/failure-flags/failure-flags'
import { type PagedResponse } from 'types/paged-response'

import type { AxiosError } from 'axios'

export type FailureFlagsExperimentResponse = PagedResponse<
  FailureFlagExperiment[]
>

// TODO: This base type is present for (almost) all API queries. It should be moved to a shared location and made into an extensible interface.
export type GetFailureFlagsExperimentsActiveParams = {
  teamId: string
}

/**
 * Query key for fetching active failure flag experiments. This key is used to cache/mutate/invalidate the query results
 * and should be available for import wherever the query is used.
 */
export const getActiveFailureFlagExperimentsQueryKey = (
  query: GetFailureFlagsExperimentsActiveParams
) => ['failure-flags/experiments/active', query] as const

// Builds the endpoint with the necessary query params
function getActiveFailureFlagExperimentsEndpointWithParams(
  endpoint: string,
  endpointParams: GetFailureFlagsExperimentsActiveParams
) {
  const { teamId } = endpointParams
  const params = new URLSearchParams()

  params.set('teamId', teamId)

  return `${endpoint}?${params.toString()}`
}

// The endpoint technically returns a paginated list. The API for this endpoint is currently set up to return all results
// regardless of the page size, so we just extract the "items" value and return it.
// TODO: evaluate how to handle pagination if the API changes
export function transformGetActiveFailureFlagExperiments(
  data: FailureFlagsExperimentResponse
) {
  if (!data) throw new Error('Unable to fetch running failure flag experiments')

  return data.items
}

function fetchActiveFailureFlagExperiments({
  queryKey: [endpoint, params]
}: QueryFunctionContext<
  ReturnType<typeof getActiveFailureFlagExperimentsQueryKey>
>) {
  const endpointWithParams = getActiveFailureFlagExperimentsEndpointWithParams(
    endpoint,
    params
  )

  return fetchQueryData<FailureFlagsExperimentResponse>(endpointWithParams, {
    // When we pass an AxiosRequestConfig with a transformResponse function, it overrides Axios
    // defaults, which originally parses the response as JSON. Mixing this default back into the
    // transformation array avoids having to parse text as JSON in a blocking manner.
    transformResponse: [
      ...[agent.defaults.transformResponse ?? []],
      transformGetActiveFailureFlagExperiments
    ].flat()
  })
}

export function useGetActiveFailureFlagExperiments(
  options?: QueryObserverOptions<
    FailureFlagsExperimentResponse,
    AxiosError,
    FailureFlagExperiment[]
  >
) {
  const { currentTeamId } = useAuth()

  return useQuery<
    FailureFlagsExperimentResponse,
    AxiosError,
    FailureFlagExperiment[],
    ReturnType<typeof getActiveFailureFlagExperimentsQueryKey>
  >({
    ...options,
    queryKey: getActiveFailureFlagExperimentsQueryKey({
      teamId: currentTeamId
    }),
    queryFn: fetchActiveFailureFlagExperiments
  })
}
